import React, { useState, useMemo, useEffect } from 'react';
import { Plus, FolderOpen, Users, Clock, Search, X, Check, Crown } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUserWorkspaces, useCreateStandaloneWorkspace } from '../hooks/useWorkspaces';
import { useAcceptWorkspaceInvitation } from '../hooks/useWorkspaceCollaborators';
import { useAcceptInvitation } from '../hooks/useTeams';
import { ConfirmModal } from '../components/ConfirmModal';
import { SimpleDocketAutocomplete } from '../components/SimpleDocketAutocomplete';
import { AuthForm } from '../components/AuthForm';
import { supabase } from '../lib/supabase';
import { userTierService } from '../lib/userTierService';
import { Workspace } from '../types';

export const WorkspacesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // React Query hooks
  const { data: workspaces = [], isLoading: loading, error: workspacesError, refetch: refetchWorkspaces } = useUserWorkspaces();
  const createWorkspaceMutation = useCreateStandaloneWorkspace();
  const acceptInvitationMutation = useAcceptWorkspaceInvitation();
  const acceptTeamInvitationMutation = useAcceptInvitation();

  const [error, setError] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [userTier, setUserTier] = useState<string>('free');
  const [showProGate, setShowProGate] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('all'); // all, owned, shared, team
  const [showAcceptTeamInvitationModal, setShowAcceptTeamInvitationModal] = useState(false);
  const [pendingTeamInvitationId, setPendingTeamInvitationId] = useState<string | null>(null);
  const [showWorkspaceInvitationAuth, setShowWorkspaceInvitationAuth] = useState(false);
  const [pendingWorkspaceInvitationId, setPendingWorkspaceInvitationId] = useState<string | null>(null);
  const [invitationEmail, setInvitationEmail] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  
  // Form states
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWorkspaceDescription, setNewWorkspaceDescription] = useState('');
  const [newWorkspaceDocketNumber, setNewWorkspaceDocketNumber] = useState('');

  // Handle errors from React Query
  React.useEffect(() => {
    if (workspacesError) {
      setError(workspacesError instanceof Error ? workspacesError.message : 'Failed to load workspaces');
    }
  }, [workspacesError]);

  const handleAcceptWorkspaceInvitation = async (workspaceId: string) => {
    try {
      setError('');
      await acceptInvitationMutation.mutateAsync(workspaceId);
      setPendingWorkspaceInvitationId(null);
      setShowWorkspaceInvitationAuth(false);
      // Clear localStorage after successful acceptance
      localStorage.removeItem('pendingWorkspaceInvitationId');
      localStorage.removeItem('pendingWorkspaceInvitationEmail');
      // Redirect to the workspace
      navigate(`/workspace/${workspaceId}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to accept invitation';
      setError(errorMessage);
      // If error is about already being a collaborator, still redirect
      if (errorMessage.includes('already') || errorMessage.includes('collaborator')) {
        // Clear localStorage even on "already collaborator" error
        localStorage.removeItem('pendingWorkspaceInvitationId');
        localStorage.removeItem('pendingWorkspaceInvitationEmail');
        navigate(`/workspace/${workspaceId}`);
      }
    }
  };

  // Check user tier for Pro gate
  useEffect(() => {
    userTierService.getUserTier().then(setUserTier).catch(() => {});
  }, []);

  const isProOrAbove = userTier === 'pro' || userTier === 'enterprise' || userTier === 'paid';

  const handleNewWorkspaceClick = () => {
    if (!isProOrAbove) {
      setShowProGate(true);
    } else {
      setShowCreateModal(true);
    }
  };

  // Check auth status and handle workspace invitation after auth
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      
      // Check localStorage for pending invitation on mount (in case of OAuth redirect)
      const storedInvitationId = localStorage.getItem('pendingWorkspaceInvitationId');
      const storedInvitationEmail = localStorage.getItem('pendingWorkspaceInvitationEmail');
      
      if (storedInvitationId) {
        setPendingWorkspaceInvitationId(storedInvitationId);
        if (storedInvitationEmail) {
          setInvitationEmail(storedInvitationEmail);
        }
        
        // If user is already authenticated, accept the invitation immediately
        if (user) {
          handleAcceptWorkspaceInvitation(storedInvitationId);
        }
      }
    };
    checkAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setUser(session?.user ?? null);
      
      // Check both state and localStorage for pending invitation
      const invitationId = pendingWorkspaceInvitationId || localStorage.getItem('pendingWorkspaceInvitationId');
      
      // If user just logged in and we have a pending workspace invitation, accept it
      if (session?.user && invitationId) {
        handleAcceptWorkspaceInvitation(invitationId);
      }
    });

    return () => subscription.unsubscribe();
  }, [pendingWorkspaceInvitationId]);

  // Handle workspace invitation from URL params
  useEffect(() => {
    const workspaceInvitationId = searchParams.get('workspaceInvitation');
    const email = searchParams.get('email');
    
    if (workspaceInvitationId) {
      setPendingWorkspaceInvitationId(workspaceInvitationId);
      
      // Persist invitation ID in localStorage so it survives OAuth redirects
      localStorage.setItem('pendingWorkspaceInvitationId', workspaceInvitationId);
      
      if (email) {
        setInvitationEmail(email);
        localStorage.setItem('pendingWorkspaceInvitationEmail', email);
      }
      
      // Clean up URL param
      searchParams.delete('workspaceInvitation');
      searchParams.delete('email');
      setSearchParams(searchParams, { replace: true });
      
      // Always show auth modal for workspace invitations
      // Users must explicitly sign in/sign up to accept the invitation
      setError('');
      setShowWorkspaceInvitationAuth(true);
    }
  }, [searchParams, setSearchParams]);

  // Handle team invitation from URL params
  useEffect(() => {
    const teamInvitationId = searchParams.get('teamInvitation');
    if (teamInvitationId) {
      setPendingTeamInvitationId(teamInvitationId);
      setShowAcceptTeamInvitationModal(true);
      // Clean up URL param
      searchParams.delete('teamInvitation');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleAuthSuccess = () => {
    // Auth was successful, the auth state change listener will handle accepting the invitation
    setShowWorkspaceInvitationAuth(false);
  };

  // Filter workspaces based on search and filters
  const filteredWorkspaces = useMemo(() => {
    let filtered = workspaces;
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(workspace => 
        workspace.name.toLowerCase().includes(query) ||
        workspace.docket_number.toLowerCase().includes(query) ||
        (workspace.description && workspace.description.toLowerCase().includes(query))
      );
    }
    
    // Type filter
    if (filterType === 'owned') {
      filtered = filtered.filter(w => w.user_role === 'owner' && w.access_type === 'collaborator');
    } else if (filterType === 'shared') {
      filtered = filtered.filter(w => w.user_role !== 'owner' && w.access_type === 'collaborator');
    } else if (filterType === 'team') {
      filtered = filtered.filter(w => w.access_type === 'team_member');
    }
    
    return filtered;
  }, [workspaces, searchQuery, filterType]);

  // Group workspaces
  const ownedWorkspaces = useMemo(() => 
    filteredWorkspaces.filter(w => w.user_role === 'owner' && w.access_type === 'collaborator'),
    [filteredWorkspaces]
  );
  
  const sharedWorkspaces = useMemo(() => 
    filteredWorkspaces.filter(w => w.user_role !== 'owner' && w.access_type === 'collaborator'),
    [filteredWorkspaces]
  );
  
  const teamWorkspaces = useMemo(() => 
    filteredWorkspaces.filter(w => w.access_type === 'team_member'),
    [filteredWorkspaces]
  );

  const handleCreateWorkspace = async () => {
    if (!newWorkspaceName.trim() || !newWorkspaceDocketNumber.trim()) {
      setError('Workspace name and docket number are required');
      return;
    }

    // Validate docket number format (extract just the docket number if it includes proceeding name)
    const docketNumber = newWorkspaceDocketNumber.trim().split(/\s+/)[0]; // Take first part (docket number)
    if (!/^[A-Z0-9\-_\.]+$/i.test(docketNumber)) {
      setError('Docket number can only contain letters, numbers, hyphens, underscores, and periods');
      return;
    }

    try {
      setError('');
      // Extract just the docket number (first part before any proceeding name)
      const docketNumber = newWorkspaceDocketNumber.trim().split(/\s+/)[0];
      
      await createWorkspaceMutation.mutateAsync({
        name: newWorkspaceName.trim(),
        description: newWorkspaceDescription.trim() || undefined,
        docketNumber: docketNumber
      });
      
      setNewWorkspaceName('');
      setNewWorkspaceDescription('');
      setNewWorkspaceDocketNumber('');
      setShowCreateModal(false);
      // Navigate to the new workspace
      // We'll need to get the workspace ID from the mutation result
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create workspace');
    }
  };

  const handleAcceptInvitation = async (workspaceId: string) => {
    try {
      setError('');
      await acceptInvitationMutation.mutateAsync(workspaceId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invitation');
    }
  };

  const handleAcceptTeamInvitation = async () => {
    if (!pendingTeamInvitationId) return;
    try {
      setError('');
      await acceptTeamInvitationMutation.mutateAsync(pendingTeamInvitationId);
      setShowAcceptTeamInvitationModal(false);
      setPendingTeamInvitationId(null);
      refetchWorkspaces();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept team invitation');
    }
  };

  const handleDeclineTeamInvitation = () => {
    setShowAcceptTeamInvitationModal(false);
    setPendingTeamInvitationId(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-purple-100 text-purple-800';
      case 'editor': return 'bg-blue-100 text-blue-800';
      case 'commenter': return 'bg-green-100 text-green-800';
      case 'viewer': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-3xl font-bold text-gray-900">My Workspaces</h1>
              </div>
              <p className="text-gray-600 mt-2">Manage your workspaces and collaborate on docket summaries</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/teams')}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium rounded-lg transition-colors"
              >
                Teams
              </button>
              <button
                onClick={handleNewWorkspaceClick}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>New Workspace</span>
              </button>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && !showWorkspaceInvitationAuth && !showProGate && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Search and Filter */}
        <div className="mb-6 bg-white rounded-lg shadow p-4">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search workspaces by name or docket number..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="all">All Workspaces</option>
              <option value="owned">Owned by Me</option>
              <option value="shared">Shared with Me</option>
              <option value="team">Team Workspaces</option>
            </select>
          </div>
        </div>

        {/* Workspaces List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading workspaces...</p>
          </div>
        ) : filteredWorkspaces.length === 0 ? (
          <div className="text-center py-12">
            <FolderOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No workspaces found</p>
            <p className="text-sm text-gray-500 mt-2">
              {searchQuery || filterType !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'Create your first workspace to start collaborating on docket summaries'}
            </p>
            {!searchQuery && filterType === 'all' && (
              <button
                onClick={handleNewWorkspaceClick}
                className="mt-4 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors"
              >
                Create Workspace
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            {/* Owned Workspaces */}
            {ownedWorkspaces.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Owned by Me</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {ownedWorkspaces.map((workspace) => (
                    <WorkspaceCard
                      key={workspace.id}
                      workspace={workspace}
                      onOpen={() => navigate(`/workspace/${workspace.id}`)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Shared Workspaces */}
            {sharedWorkspaces.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Shared with Me</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {sharedWorkspaces.map((workspace) => (
                    <WorkspaceCard
                      key={workspace.id}
                      workspace={workspace}
                      onOpen={() => navigate(`/workspace/${workspace.id}`)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Team Workspaces */}
            {teamWorkspaces.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Team Workspaces</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {teamWorkspaces.map((workspace) => (
                    <WorkspaceCard
                      key={workspace.id}
                      workspace={workspace}
                      onOpen={() => navigate(`/workspace/${workspace.id}`)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Workspace Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Create New Workspace</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fld-WorkspacesPagetsx-0">
                    Workspace Name *
                  </label>
                  <input
                    type="text"
                    value={newWorkspaceName}
                    id="fld-WorkspacesPagetsx-0" onChange={(e) => setNewWorkspaceName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter workspace name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fld-WorkspacesPagetsx-1">
                    Docket Number *
                  </label>
                  <SimpleDocketAutocomplete
                    value={newWorkspaceDocketNumber}
                    onChange={(value) => {
                      setNewWorkspaceDocketNumber(value);
                      // If workspace name is empty and we have a docket number, suggest a default name
                      if (value && !newWorkspaceName.trim()) {
                        const docketMatch = value.match(/^([A-Z0-9\-_\.]+)/i);
                        if (docketMatch) {
                          setNewWorkspaceName(`FCC ${docketMatch[1]}`);
                        }
                      }
                    }}
                    onSubmit={(value) => {
                      setNewWorkspaceDocketNumber(value);
                    }}
                    placeholder="Search by docket number or proceeding name (e.g., 23-320, 6 GHz NPRM)"
                    className="w-full"
                    showIcon={true}
                  />
                </div>
                
                <div>
                  <label htmlFor="fld2-WorkspacesPagetsx-0" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea id="fld2-WorkspacesPagetsx-0"
                    value={newWorkspaceDescription}
                    onChange={(e) => setNewWorkspaceDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter workspace description (optional)"
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateWorkspace}
                  disabled={createWorkspaceMutation.isPending}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md transition-colors disabled:opacity-50"
                >
                  {createWorkspaceMutation.isPending ? 'Creating...' : 'Create Workspace'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Workspace Invitation Auth Modal */}
      {showWorkspaceInvitationAuth && pendingWorkspaceInvitationId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Sign In to Accept Invitation</h3>
              {/* X button removed - users must sign in to accept invitation */}
            </div>
            
            <div className="p-6">
              <p className="text-gray-700 mb-6">
                {invitationEmail 
                  ? `Sign in or create an account with ${invitationEmail} to accept this workspace invitation.`
                  : 'Sign in or create an account to accept this workspace invitation.'}
              </p>
              
              <AuthForm
                onCancel={() => {
                  // No-op: Users must sign in to accept invitation, cannot cancel
                }}
                onSuccess={handleAuthSuccess}
                initialEmail={invitationEmail || ''}
                emailRedirectTo={`${window.location.origin}${window.location.pathname}${window.location.search}`}
                headline="Sign in to accept your invitation"
                subhead="Enter your email and we'll send you a magic link to finish joining this workspace."
              />
            </div>
          </div>
        </div>
      )}

      {/* Team Invitation Acceptance Modal */}
      {showAcceptTeamInvitationModal && pendingTeamInvitationId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Team Invitation</h3>
              <button
                onClick={() => {
                  setShowAcceptTeamInvitationModal(false);
                  setPendingTeamInvitationId(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-gray-700 mb-6">
                You've been invited to join a team. Accepting will give you access to all team workspaces.
              </p>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={handleDeclineTeamInvitation}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Decline
                </button>
                <button
                  onClick={handleAcceptTeamInvitation}
                  disabled={acceptTeamInvitationMutation.isPending}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md transition-colors disabled:opacity-50"
                >
                  {acceptTeamInvitationMutation.isPending ? 'Accepting...' : 'Accept Invitation'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pro Gate Modal */}
      {showProGate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-8 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-green-50 mb-4">
              <Crown className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Pro Required</h3>
            <p className="text-gray-500 text-sm mb-6">
              Workspaces and team collaboration are available on the Pro plan ($199/month) and above.
              Upgrade to create workspaces and collaborate with your team.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => { setShowProGate(false); navigate('/pricing'); }}
                className="w-full px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium text-sm"
              >
                View Pricing
              </button>
              <button
                onClick={() => setShowProGate(false)}
                className="w-full px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Workspace Card Component
interface WorkspaceCardProps {
  workspace: Workspace & { user_role: string; access_type: string };
  onOpen: () => void;
}

const WorkspaceCard: React.FC<WorkspaceCardProps> = ({ workspace, onOpen }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-purple-100 text-purple-800';
      case 'editor': return 'bg-blue-100 text-blue-800';
      case 'commenter': return 'bg-green-100 text-green-800';
      case 'viewer': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (e.currentTarget as HTMLElement).click(); } }} 
      className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-6 cursor-pointer border border-gray-200 hover:border-orange-300"
      onClick={onOpen}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">{workspace.name}</h3>
          <p className="text-sm text-gray-600 mb-2">Docket {workspace.docket_number}</p>
          {workspace.description && (
            <p className="text-sm text-gray-500 line-clamp-2">{workspace.description}</p>
          )}
        </div>
        <span className={`px-2 py-1 text-xs rounded-full ${getRoleBadgeColor(workspace.user_role)}`}>
          {workspace.user_role}
        </span>
      </div>
      
      <div className="flex items-center justify-between text-xs text-gray-500 mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center space-x-1">
          <Clock className="w-3 h-3" />
          <span>Updated {formatDate(workspace.updated_at)}</span>
        </div>
        {workspace.access_type === 'team_member' && (
          <div className="flex items-center space-x-1">
            <Users className="w-3 h-3" />
            <span>Team</span>
          </div>
        )}
      </div>
    </div>
  );
};

